import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IStoreState } from "..";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  IAllocationHistory,
  IConsolidatedMrpAlloc,
  IGroupedMrpAllocation,
  IMrpAllocation,
  IMrpAllocationsState,
  IMrpPartCodeDescription,
  IPrRequest,
} from "./mrp-allocations.types";
import {
  SERVER_GET_MRP_ALLOCATION_ROUTE,
  SERVER_GET_MRP_PART_CODE_DESCRIPTION_ROUTE,
  SERVER_UPSERT_MRP_ALLOCATION_ROUTE,
} from "./mrp-allocations.constants";

export const FETCH_MRP_ALLOCATION_LIST_PROGRESS =
  "FETCH_MRP_ALLOCATION_LIST_PROGRESS";
export const FETCH_MRP_ALLOCATION_LIST_SUCCESS =
  "FETCH_MRP_ALLOCATION_LIST_SUCCESS";
export const FETCH_MRP_ALLOCATION_LIST_FAILED =
  "FETCH_MRP_ALLOCATION_LIST_FAILED";

export const fetchMrpAllocationListProgress = () =>
  action(FETCH_MRP_ALLOCATION_LIST_PROGRESS);

export const fetchMrpAllocationListSuccess = (
  data: IMrpAllocationsState["mrp_allocations_list"]["data"],
  totalRecords: number,
) => action(FETCH_MRP_ALLOCATION_LIST_SUCCESS, { data, totalRecords });
export const fetchMrpAllocationListFailed = () =>
  action(FETCH_MRP_ALLOCATION_LIST_FAILED);

const execGroupData = (data: IMrpAllocation[]): IGroupedMrpAllocation[] => {
  const groupedData = Object.values(
    data.reduce((acc, obj) => {
      //@ts-ignore
      if (!acc[obj.order_no]) {
        //@ts-ignore
        acc[obj.order_no] = {
          id: obj.order_no,
          ...obj,
          childs: [obj],
        };
      } else {
        //@ts-ignore
        acc[obj.order_no].childs.push(obj);
      }
      return acc;
    }, {}),
  );
  return groupedData as IGroupedMrpAllocation[];
};

export const fetchMrpAllocationListAsync =
  (queryParams: IQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      let finalUrl = `${SERVER_GET_MRP_ALLOCATION_ROUTE}?${searchQuery}`;
      dispatch(fetchMrpAllocationListProgress());
      const res = await api.get(finalUrl);
      const data: IMrpAllocationsState["mrp_allocations_list"]["data"] =
        res.data.data;

      const totalRecords = res.data.totalRecords;
      const finalData = execGroupData(data);

      dispatch(fetchMrpAllocationListSuccess(finalData, totalRecords));
    } catch (err: any) {
      dispatch(fetchMrpAllocationListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };
// ------------------------------------------------------------------------------------------
export const FETCH_ORDER_NO_MRP_ALLOCATIONS_PROGRESS =
  "FETCH_ORDER_NO_MRP_ALLOCATIONS_PROGRESS";
export const FETCH_ORDER_NO_MRP_ALLOCATIONS_SUCCESS =
  "FETCH_ORDER_NO_MRP_ALLOCATIONS_SUCCESS";
export const FETCH_ORDER_NO_MRP_ALLOCATIONS_FAILED =
  "FETCH_ORDER_NO_MRP_ALLOCATIONS_FAILED";

export const fetchSingleOrderMrpAllocationsProgress = () =>
  action(FETCH_ORDER_NO_MRP_ALLOCATIONS_PROGRESS);

export const fetchSingleOrderMrpAllocationsSuccess = (
  data: IMrpAllocationsState["single_order_mrp_allocations"]["data"],
  totalRecords: number,
) => action(FETCH_ORDER_NO_MRP_ALLOCATIONS_SUCCESS, { data, totalRecords });
export const fetchSingleOrderMrpAllocationsFailed = () =>
  action(FETCH_ORDER_NO_MRP_ALLOCATIONS_FAILED);

export const fetchSingleOrderMrpAllocationsAsync =
  (order_no: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let finalUrl = `${SERVER_GET_MRP_ALLOCATION_ROUTE}?order_no=${order_no}`;
      dispatch(fetchSingleOrderMrpAllocationsProgress());
      const res = await api.get(finalUrl);
      const data: IMrpAllocationsState["single_order_mrp_allocations"]["data"] =
        res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchSingleOrderMrpAllocationsSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchSingleOrderMrpAllocationsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchMrpAllocationApprovalsListAsync =
  (queryParams: IQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      let finalUrl = `/approval/get-approval${searchQuery}&table_name=latest_mrp_allocation&status=REQUESTED`;
      dispatch(fetchMrpAllocationListProgress());
      const res = await api.get(finalUrl);
      const data: IMrpAllocationsState["mrp_allocations_list"]["data"] =
        res.data.data;

      const totalRecords = res.data.totalRecords;

      dispatch(fetchMrpAllocationListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchMrpAllocationListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_MRP_ALLOCATION_PROGRESS = "FETCH_MRP_ALLOCATION_PROGRESS";
export const FETCH_MRP_ALLOCATION_SUCCESS = "FETCH_MRP_ALLOCATION_SUCCESS";
export const FETCH_MRP_ALLOCATION_FAILED = "FETCH_MRP_ALLOCATION_FAILED";

export const fetchMrpAllocationProgress = () =>
  action(FETCH_MRP_ALLOCATION_PROGRESS);
export const fetchMrpAllocationSuccess = (data: IMrpAllocation, meta: string) =>
  action(FETCH_MRP_ALLOCATION_SUCCESS, { data, meta });
export const fetchMrpAllocationFailed = (errorMessage: string) =>
  action(FETCH_MRP_ALLOCATION_FAILED, { errorMessage });

export const fetchSingleMrpAllocationAsync =
  (uuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchMrpAllocationProgress());
      const res = await api.get(
        `${SERVER_GET_MRP_ALLOCATION_ROUTE}?mrp_allocation_uuid=${uuid}&stock_info=TRUE`,
      );
      const data: IMrpAllocation[] = res.data.data;
      const meta: string = res.data.meta;
      if (data.length > 0) {
        dispatch(fetchMrpAllocationSuccess(data[0], meta));
      } else {
        dispatch(
          fetchMrpAllocationFailed("Oops! We couldn't find any records."),
        );
      }
    } catch (err: any) {
      dispatch(fetchMrpAllocationFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertSingleMrpAllocationAsync =
  (
    data: IMrpAllocation,
    onCallback: (isSuccess: boolean, deviation?: IMrpAllocation) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        remark,
        create_ts,
        insert_ts,
        rev_no,
        approval_uuid,
        requested_by_uuid,
        is_user_approver,
        ...payload
      } = data;

      dispatch(saveLoaderProgress());
      const res = await api.post(SERVER_UPSERT_MRP_ALLOCATION_ROUTE, payload);
      let message = res.data.message;
      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertRequestMrpAsync =
  (
    uuid: string,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/planning/upsert-mrp-from-routing", {
        routing_uuid: uuid,
      });
      let message = "MRP requested successfully!";
      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export interface IUpsertPrRequest {
  project_uuid: string;
  pr_items: {
    product_uuid: string;
    product_name: string;
    pr_quantity: number;
  }[];
  material_request_uuid?: string;
}

export const upsertPrRequestAsync =
  (
    data: IUpsertPrRequest,
    onCallback: (isSuccess: boolean, deviation?: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/grn/upsert-pr-request", data);

      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: res.data.message || "PR requested successfully!",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to create PR Request",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
// *************** MRP allocation revision ************

export const fetchMRPRevisionAsync =
  (
    uuid: string,
    revNo: number,
    onCallback: (isSuccess: boolean, data: IMrpAllocation) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/mrp/get-mrp-revision?mrp_allocation_uuid=${uuid}&rev_no=${revNo}`,
      );
      const data: IMrpAllocation[] = res.data.data;
      if (data.length > 0) {
        onCallback(true, data[0]);
      }
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertMRPRevisionAsync =
  (
    mrdUuid: string,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/mrp/upsert-mrp-revision", {
        mrp_allocation_uuid: mrdUuid,
      });
      let message = "Revision requested successfully!";
      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// mrp allocation history
export const fetchAllocationHistoryAsync =
  (
    part_code: string,
    onCallback: (isSuccess: boolean, data: IAllocationHistory[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/mrp/get-allocations?part_code=${part_code}&stock_info=TRUE`,
      );
      const data: IAllocationHistory[] = res.data.data;
      if (data.length > 0) {
        onCallback(true, data);
      }
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };
export const fetchAllocationHistoryListAsync =
  (
    uuid: string,
    onCallback: (isSuccess: boolean, data: IAllocationHistory[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/mrp/get-allocations?mrp_allocation_uuid=${uuid}&stock_info=TRUE`,
      );
      const data: IAllocationHistory[] = res.data.data;
      if (data.length > 0) {
        onCallback(true, data);
      }
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_MRP_ALLOCATION = "CLEAR_MRP_ALLOCATION";
export const CLEAR_MRP_ALLOCATION_STATE = "CLEAR_MRP_ALLOCATION_STATE";
export const clearSingleMrpAllocationSync = () => action(CLEAR_MRP_ALLOCATION);
export const clearMrpAllocationsState = () =>
  action(CLEAR_MRP_ALLOCATION_STATE);

// ###########################################################################################
// ################################## Part code description ##################################
// ###########################################################################################

export const FETCH_MRP_PART_CODE_DESCRIPTION_PROGRESS =
  "FETCH_MRP_PART_CODE_DESCRIPTION_PROGRESS";
export const FETCH_MRP_PART_CODE_DESCRIPTION_SUCCESS =
  "FETCH_MRP_PART_CODE_DESCRIPTION_SUCCESS";
export const FETCH_MRP_PART_CODE_DESCRIPTION_FAILED =
  "FETCH_MRP_PART_CODE_DESCRIPTION_FAILED";
export const fetchMrpPartCodeDescriptionProgress = () =>
  action(FETCH_MRP_PART_CODE_DESCRIPTION_PROGRESS);
export const fetchMrpPartCodeDescriptionSuccess = (
  data: IMrpPartCodeDescription,
) => action(FETCH_MRP_PART_CODE_DESCRIPTION_SUCCESS, { data });
export const fetchMrpPartCodeDescriptionFailed = (errorMessage: string) =>
  action(FETCH_MRP_PART_CODE_DESCRIPTION_FAILED, { errorMessage });

export const fetchSingleMrpPartCodeDescriptionAsync =
  (part_code: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchMrpPartCodeDescriptionProgress());
      const res = await api.get(
        `${SERVER_GET_MRP_PART_CODE_DESCRIPTION_ROUTE}?part_code=${part_code}`,
      );
      const data: IMrpPartCodeDescription[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchMrpPartCodeDescriptionSuccess(data[0]));
      } else {
        dispatch(
          fetchMrpPartCodeDescriptionFailed(
            "Oops! We couldn't find any records.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(
        fetchMrpPartCodeDescriptionFailed("Something went to be wrong!"),
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

// ######################################  Consolidated Mrp  Actions ########################################
export const FETCH_ORDER_CONSOLIDATED_MRP_LIST_PROGRESS =
  "FETCH_ORDER_CONSOLIDATED_MRP_LIST_PROGRESS";
export const FETCH_ORDER_CONSOLIDATED_MRP_LIST_SUCCESS =
  "FETCH_ORDER_CONSOLIDATED_MRP_LIST_SUCCESS";
export const FETCH_ORDER_CONSOLIDATED_MRP_LIST_FAILED =
  "FETCH_ORDER_CONSOLIDATED_MRP_LIST_FAILED";

export const fetchSingleOrderConsolidatedMrpProgress = () =>
  action(FETCH_ORDER_CONSOLIDATED_MRP_LIST_PROGRESS);

export const fetchSingleOrderConsolidatedMrpSuccess = (
  data: IConsolidatedMrpAlloc[],
  totalRecords: number,
) => action(FETCH_ORDER_CONSOLIDATED_MRP_LIST_SUCCESS, { data, totalRecords });
export const fetchSingleOrderConsolidatedMrpFailed = () =>
  action(FETCH_ORDER_CONSOLIDATED_MRP_LIST_FAILED);

export const fetchSingleOrderConsolidatedMrpAsync =
  (project_uuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      let finalUrl = `/grn/get-consolidated-mr?project_uuid=${project_uuid}`;
      dispatch(fetchSingleOrderConsolidatedMrpProgress());

      const res = await api.get(finalUrl);
      const data: IConsolidatedMrpAlloc[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchSingleOrderConsolidatedMrpSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchSingleOrderConsolidatedMrpFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch allocation records",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertSingleOrderConsolidatedMrpAsync =
  (
    payload: {
      project_uuid?: string;
      billing_company_uuid?: string;
      billing_company_name?: string;
      product_details: IConsolidatedMrpAlloc[];
    },
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      await api.post("/grn/upsert-consolidated-mr-allocations", payload);

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: "Data Updated successfully!",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",

          message:
            err?.response?.data?.message ||
            err?.message ||
            "Network Error: Unable to reach server",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
